import React from "react";
import PropTypes from "prop-types";

import DataGrid from "../../Components/DataGrid";
import { getEltAndAccessor, getEltAndAccessorDate } from "./common/helpers";
import DateFilterElt from "./common/DateFilterElt";
import HeaderElt from "./common/HeaderElt";
import IpFilterElt from "./common/IpFilterElt";

import "./common/ResourcesTable.scss";

/*
Constants and Helpers
*/

// The list of the table's filter keys
export const TABLE_FILTERS = ["address", "created", "firstSeen", "lastSeen"];

export const getRows = ({ data, onClickElt = () => () => {} }) => {
  const rows = data.map((elt) => {
    const { address, created, firstSeen, id: ipId, lastSeen } = elt;

    // Address
    const { accessor: addressAccessor, element: addressElt } =
      getEltAndAccessor({
        clickable: !!ipId,
        label: address,
        onClickElt: onClickElt(ipId),
      });

    // Created
    const { accessor: createdAccessor, element: createdElt } =
      getEltAndAccessorDate(created);

    // First Seen
    const { accessor: firstSeenAccessor, element: firstSeenElt } =
      getEltAndAccessorDate(firstSeen);

    // Last Seen
    const { accessor: lastSeenAccessor, element: lastSeenElt } =
      getEltAndAccessorDate(lastSeen);

    return {
      address: <strong>{addressElt}</strong>,
      addressAccessor,

      created: createdElt,
      createdAccessor,

      firstSeen: firstSeenElt,
      firstSeenAccessor,

      lastSeen: lastSeenElt,
      lastSeenAccessor,
    };
  });

  return rows;
};

const getColumns = ({ filters = {}, setFilter, setSorting, sorting }) => {
  const { addressValue, createdOption, firstSeenOption, lastSeenOption } =
    filters;

  return [
    {
      Header: (
        <HeaderElt
          fieldName="address"
          label="Address"
          setSorting={setSorting}
          sorting={sorting}
        />
      ),
      accessor: "addressAccessor",
      filterable: true,
      sortable: false,
      Cell: (row) => row.original.address,
      maxWidth: 220,
      minWidth: 180,
      Filter: (
        <IpFilterElt
          fieldName="address"
          filterValue={addressValue}
          setFilter={setFilter}
        />
      ),
      filterMethod: () => true,
    },

    {
      Header: (
        <HeaderElt
          fieldName="firstSeen"
          label="First Seen"
          setSorting={setSorting}
          sorting={sorting}
        />
      ),
      accessor: "firstSeenAccessor",
      filterable: true,
      sortable: false,
      Cell: (row) => row.original.firstSeen,
      maxWidth: 220,
      minWidth: 180,
      Filter: (
        <DateFilterElt
          fieldName="firstSeen"
          optionKey={firstSeenOption}
          setFilter={setFilter}
        />
      ),
      filterMethod: () => true,
    },

    {
      Header: (
        <HeaderElt
          fieldName="created"
          label="Tracked Since"
          setSorting={setSorting}
          sorting={sorting}
        />
      ),
      accessor: "createdAccessor",
      filterable: true,
      sortable: false,
      Cell: (row) => row.original.created,
      maxWidth: 220,
      minWidth: 180,
      Filter: (
        <DateFilterElt
          fieldName="created"
          optionKey={createdOption}
          setFilter={setFilter}
        />
      ),
      filterMethod: () => true,
    },

    {
      Header: (
        <HeaderElt
          fieldName="lastSeen"
          label="Last Seen"
          setSorting={setSorting}
          sorting={sorting}
        />
      ),
      accessor: "lastSeenAccessor",
      filterable: true,
      sortable: false,
      Cell: (row) => row.original.lastSeen,
      maxWidth: 220,
      minWidth: 180,
      Filter: (
        <DateFilterElt
          fieldName="lastSeen"
          optionKey={lastSeenOption}
          setFilter={setFilter}
        />
      ),
      filterMethod: () => true,
    },
  ];
};

/*
Component
*/

const IPsTable = (props) => {
  const {
    data,
    filters,
    pageSize,
    setFilter,
    sorting = {},
    setSorting = () => {},
  } = props;

  const columns = getColumns({
    sorting,
    filters,
    setFilter,
    setSorting,
  });

  return (
    <div className="resources-table blu-paginated">
      <DataGrid
        data={data}
        pageSize={pageSize}
        columns={columns}
        showPagination={false}
      />
    </div>
  );
};

IPsTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({})),
  filters: PropTypes.shape({}),
  pageSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setFilter: PropTypes.func,
  setSorting: PropTypes.func,
  sorting: PropTypes.shape({}),
};

IPsTable.defaultProps = {
  filters: {},
  data: [],
  pageSize: 25,
  setFilter: () => {},
  setSorting: () => {},
  sorting: {},
};

export default IPsTable;
